import type { NivelClassico } from '../types';

/** Catálogo de níveis por Elo aproximado (nomenclatura exibida). */
export interface MetaNivel {
  id: NivelClassico;
  /** Rótulo na UI (Elo ou "Máximo"). */
  nome: string;
  /** Subtítulo curto. */
  detalhe: string;
  /** Elo-alvo para limitação de força (ignorado no máximo). */
  elo: number | null;
  /** Skill Level UCI 0–20. */
  skill: number;
  /** Limitação UCI_LimitStrength. */
  limitarForca: boolean;
  /** Tempo de pensamento base (ms). */
  movetime: number;
  /** Timeout da promessa (ms). */
  timeout: number;
  /** Hash (MB) no browser. */
  hash: number;
  /**
   * Abaixo do mínimo UCI (~1320), usamos MultiPV e escolhemos lances mais fracos.
   * 1 = só o melhor; valores altos = mais aleatoriedade fraca.
   */
  multiPvFraco: number;
}

export const CATALOGO_NIVEIS: MetaNivel[] = [
  { id: '400', nome: 'Fraldinha', detalhe: '~400 · quase aleatório', elo: 400, skill: 0, limitarForca: true, movetime: 80, timeout: 8_000, hash: 16, multiPvFraco: 16 },
  { id: '800', nome: '800', detalhe: 'Aprendiz · erros frequentes', elo: 800, skill: 0, limitarForca: true, movetime: 100, timeout: 10_000, hash: 32, multiPvFraco: 10 },
  { id: '1000', nome: '1000', detalhe: 'Iniciante · lances simples', elo: 1000, skill: 0, limitarForca: true, movetime: 120, timeout: 10_000, hash: 48, multiPvFraco: 7 },
  { id: '1200', nome: '1200', detalhe: 'Clube amador', elo: 1200, skill: 1, limitarForca: true, movetime: 160, timeout: 12_000, hash: 64, multiPvFraco: 4 },
  { id: '1400', nome: '1400', detalhe: 'Clube sólido', elo: 1400, skill: 3, limitarForca: true, movetime: 220, timeout: 12_000, hash: 64, multiPvFraco: 1 },
  { id: '1600', nome: '1600', detalhe: 'Intermediário', elo: 1600, skill: 5, limitarForca: true, movetime: 320, timeout: 15_000, hash: 96, multiPvFraco: 1 },
  { id: '1800', nome: '1800', detalhe: 'Avançado', elo: 1800, skill: 8, limitarForca: true, movetime: 450, timeout: 18_000, hash: 128, multiPvFraco: 1 },
  { id: '2000', nome: '2000', detalhe: 'Competitivo', elo: 2000, skill: 11, limitarForca: true, movetime: 650, timeout: 20_000, hash: 128, multiPvFraco: 1 },
  { id: '2200', nome: '2200', detalhe: 'Candidato a mestre', elo: 2200, skill: 15, limitarForca: true, movetime: 1_000, timeout: 25_000, hash: 256, multiPvFraco: 1 },
  { id: '2500', nome: '2500', detalhe: 'Mestre · sem freio parcial', elo: 2500, skill: 18, limitarForca: true, movetime: 1_800, timeout: 30_000, hash: 512, multiPvFraco: 1 },
  { id: 'maximo', nome: 'Máximo', detalhe: 'Força plena · 12–30 s', elo: null, skill: 20, limitarForca: false, movetime: 12_000, timeout: 60_000, hash: 1024, multiPvFraco: 1 }
];

export const NIVEIS_PADRAO: NivelClassico[] = CATALOGO_NIVEIS.map((n) => n.id);

const porId = Object.fromEntries(CATALOGO_NIVEIS.map((n) => [n.id, n])) as Record<NivelClassico, MetaNivel>;

export function metaNivel(nivel: NivelClassico): MetaNivel {
  return porId[nivel] ?? porId['1200'];
}

/** Níveis legados → Elo novo (partidas salvas / storage). */
const LEGADO: Record<string, NivelClassico> = {
  iniciante: '1200',
  casual: '1600',
  intermediario: '2000',
  experiente: '2200',
  forte: '2500',
  elite: 'maximo',
  maximo: 'maximo'
};

export function normalizarNivel(valor: unknown): NivelClassico {
  if (typeof valor === 'string' && valor in porId) return valor as NivelClassico;
  if (typeof valor === 'string' && valor in LEGADO) return LEGADO[valor];
  return '1200';
}

/**
 * Escolhe um lance entre as linhas MultiPV ranqueadas (ordem 1 = melhor).
 * Elo mais baixo favorece linhas piores — para níveis abaixo do mínimo UCI (~1320).
 */
export function escolherJogadaFraca(
  linhas: Array<{ ordem: number; jogada: string }>,
  eloAlvo: number
): string | null {
  if (!linhas.length) return null;
  const ordenadas = [...linhas]
    .filter((l) => l.jogada && /^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(l.jogada))
    .sort((a, b) => a.ordem - b.ordem);
  if (!ordenadas.length) return null;
  if (ordenadas.length === 1 || eloAlvo >= 1320) return ordenadas[0].jogada.toLowerCase();

  // 400/Fraldinha → fraqueza máxima; 1320 → 0
  const fraqueza = Math.max(0, Math.min(1, (1320 - eloAlvo) / 920));
  // p baixo → índice alto (lance pior) com mais frequência
  const p = 2.2 - fraqueza * 2.05;
  let idx = Math.min(ordenadas.length - 1, Math.floor(Math.pow(Math.random(), p) * ordenadas.length));
  // Fraldinha: ~35% chance de pegar um dos 3 piores lances da lista
  if (eloAlvo <= 400 && ordenadas.length >= 3 && Math.random() < 0.35) {
    idx = ordenadas.length - 1 - Math.floor(Math.random() * Math.min(3, ordenadas.length));
  }
  return ordenadas[idx].jogada.toLowerCase();
}
